import { useEffect, useState } from "react";
import { Button, Card, Icon, Loading } from "@/components/atoms";
import { FlightTrackingModal } from "@/components/organisms/AddOns";
import { useOperator } from "@/hooks";
import { useGetPremiumAddOnsQuery } from "@/redux/apis/addOn/addOnApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { addOnItemSelector } from "@/redux/slices/addOn/selectors";
import { setAction } from "@/redux/slices/booking/bookingSlice";
import { BookingDetails, BookingFlight } from "@/redux/slices/booking/types";
import { clsx } from "@/utils";
import { arrivalFlightOf, departureFlightOf } from "../../../helpers/stopFlights";
import { BookingFlightStatusCode } from "../../../common/BookingFlightStatusCode/BookingFlightStatusCode";
import { DetailField } from "../../../common/DetailField";

/**
 * One flight's card. Identical treatment for both legs — live status chip, cancelled styling,
 * airline + number + terminal — because a departure is tracked exactly as an arrival is (status,
 * terminal, cancellation notice). What differs is which leg the TERMINAL comes off, and whether the
 * card offers the drill-in.
 */
const FlightCard = ({
  flight,
  label,
  terminal,
  isEnabled,
  isFetching,
  action,
}: {
  flight: BookingFlight;
  label: string;
  /** The terminal for THIS leg — see `FlightSection` for why it is passed rather than derived here. */
  terminal: string | null;
  isEnabled?: boolean;
  isFetching: boolean;
  action?: JSX.Element;
}) => {
  // `scheduled` is not "we know nothing" — it is where every flight booking sits until roughly three
  // days out, carrying a published schedule the whole time. Only not_found and unknown carry nothing.
  const hasData = flight.status !== "not_found" && flight.status !== "unknown";
  const statusClasses = {
    "is-enabled": isEnabled,
    "has-data": hasData || !isEnabled,
    "is-cancelled": flight.status === "cancelled",
  };

  return (
    <div className="group/flight-unlock relative">
      {isFetching && <Loading className="z-30 rounded-lg" />}
      <Card className={clsx("relative p-2.5", statusClasses)}>
        <div className="absolute right-2 top-2">
          {isEnabled && <BookingFlightStatusCode status={flight.status} delay={flight.delay} type="default" />}
        </div>

        <DetailField
          leading={<Icon name="Airplane" size="lg" className="text-neutral-dark-gray" />}
          label={label}
          value={`${flight.airline ? flight.airline.name : ""} (${flight.number})${terminal ? ` - Terminal ${terminal}` : ""}`}
        />

        {action && <div className="mt-3 flex justify-stretch">{action}</div>}
      </Card>
    </div>
  );
};

interface FlightSectionProps {
  booking: BookingDetails;
  onModalStateChange?: (isOpen: boolean) => void;
}

/**
 * The flight card(s) on the operator's booking detail panel — the dedicated home for flight display,
 * which is why the DEPARTURE lives here rather than as loose rows in `BasicDetailsSection`.
 *
 * A job can carry one at each end (DEV-1487), so both render, and BOTH name their leg — "Flight
 * Details (Arrival)" and "Flight Details (Departure)". Parallel construction on purpose: labelling one
 * by the artefact and the other by the leg left nothing signalling they are the same kind of thing.
 * The leg is named even on an arrival-only booking, which is the common case and therefore the one
 * that sets the habit; shortening it there would cost more in inconsistency than it saves in width.
 *
 * THE TERMINAL COMES OFF ITS OWN LEG. `flight.arrival.terminal` for the arrival, `flight.departure.
 * terminal` for the departure — the same trap `stopFlights` exists to close: read off the wrong leg,
 * a drop-off booking shows the terminal at the far end of the route, which is not where the driver is
 * taking anyone.
 */
export const FlightSection = ({ booking, onModalStateChange }: FlightSectionProps) => {
  const dispatch = useAppDispatch();
  const { roles } = useOperator();
  const { isFetching } = useGetPremiumAddOnsQuery();
  const flightTracking = useAppSelector(addOnItemSelector("flight-tracking"));
  const isEnabled = flightTracking?.enabled;
  const isAdmin = roles.includes("Fa_Access") || roles.includes("Owner") || roles.includes("Admin");
  const [step, setStep] = useState<"add-on" | undefined>(undefined);
  const arrival = arrivalFlightOf(booking.stops);
  const departure = departureFlightOf(booking.stops);

  useEffect(() => {
    onModalStateChange?.(Boolean(step));
  }, [step, onModalStateChange]);

  const handleClick = () => {
    if (isEnabled) dispatch(setAction("flight-details"));
  };

  const handleDepartureClick = () => {
    if (isEnabled) dispatch(setAction("departure-flight-details"));
  };

  // EITHER flight is reason enough to render: a drop-off booking carries only a departure, and
  // returning null on it is what left the whole panel silent about the flight (staging J000527).
  if (!arrival && !departure) return null;

  return (
    <>
      {arrival && (
        <FlightCard
          flight={arrival}
          label="Flight Details (Arrival)"
          terminal={arrival.arrival.terminal}
          isEnabled={isEnabled}
          isFetching={isFetching}
          action={
            isEnabled ? (
              <Button
                variant="tertiary-light"
                size="sm"
                endIcon="ArrowRight"
                onClick={handleClick}
                className="text-info hover:bg-transparent hover:text-info-dark"
              >
                View Flight Details
              </Button>
            ) : (
              <Button
                endIcon={<Icon name="Diamonds" variant="Bold" size="md" className="text-warning" />}
                variant="primary"
                onClick={() => setStep("add-on")}
                className="w-full"
              >
                Get Flight Tracking
              </Button>
            )
          }
        />
      )}

      {/* The departure drills into the SAME panel, told which leg it is describing — so it reads the
          departure's airport, scheduled departure and terminal, and never the word "Landing".

          The upsell is not REPEATED — it belongs to the section and the arrival card carries it — but
          it does fall through to here when there is no arrival card to carry it. That state is
          reachable despite `getDepartureFlightData` gating on the add-on: an operator who turns
          tracking off after the booking was made still has the stored flight, and a card with no
          chip, no drill-in and no way to re-enable either is a dead end. */}
      {departure && (
        <FlightCard
          flight={departure}
          label="Flight Details (Departure)"
          terminal={departure.departure.terminal}
          isEnabled={isEnabled}
          isFetching={isFetching}
          action={
            isEnabled ? (
              <Button
                variant="tertiary-light"
                size="sm"
                endIcon="ArrowRight"
                onClick={handleDepartureClick}
                className="text-info hover:bg-transparent hover:text-info-dark"
              >
                View Flight Details
              </Button>
            ) : arrival ? undefined : (
              <Button
                endIcon={<Icon name="Diamonds" variant="Bold" size="md" className="text-warning" />}
                variant="primary"
                onClick={() => setStep("add-on")}
                className="w-full"
              >
                Get Flight Tracking
              </Button>
            )
          }
        />
      )}

      <FlightTrackingModal open={step === "add-on"} onClose={() => setStep(undefined)} isAdmin={isAdmin} />
    </>
  );
};
